import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { useChangeGroupMutation } from "@hd/api/auth/mutations/useChangeGroupMutation";
import { useConfirmMfaSetupMutation } from "@hd/api/auth/mutations/useConfirmMfaSetupMutation";
import { useForgotPasswordMutation } from "@hd/api/auth/mutations/useForgotPasswordMutation";
import { useLoginMutation } from "@hd/api/auth/mutations/useLoginMutation";
import { useLogoutMutation } from "@hd/api/auth/mutations/useLogoutMutation";
import { useResetPasswordMutation } from "@hd/api/auth/mutations/useResetPasswordMutation";
import { useStartMfaSetupMutation } from "@hd/api/auth/mutations/useStartMfaSetupMutation";
import { useVerifyMfaMutation } from "@hd/api/auth/mutations/useVerifyMfaMutation";
import { refreshTokenRequest } from "@hd/api/auth/requests/refresh-token-request";
import { signUpRequest } from "@hd/api/auth/requests/signup-request";
import { StepUpRequest } from "@hd/api/auth/requests/step-up-request";
import { processMfaQueue } from "@hd/http-client/HttpClientInterceptorProvider";
import { ApiError } from "@hd/http-client/types";
import { useTranslate } from "@hd/i18n/useTranslate";
import { showErrorFeedback } from "@hd/toast/toasts";
import { useQueryClient } from "@tanstack/react-query";

import { useAuthStore } from "./auth.store";
import { AuthenticationStatus } from "./types/authentication-status";

// Queste funzioni non dipendono da altri hook, per cui possono essere portate fuori dall'hook per evitare
// la creazione di nuove istanze ad ogni render
const setToken = (token: string | null) => {
  if (token) {
    localStorage.setItem("token", token);
  } else {
    localStorage.removeItem("token");
  }
};
const getToken = () => {
  return localStorage.getItem("token");
};
const refreshToken = async () => {
  const result = await refreshTokenRequest();
  setToken(result.jwtToken!);
};

/**
 * Hook per la gestione dell'autenticazione.
 *
 * @returns un oggetto contenente le seguenti proprietà:
 *
 * - `pageCompanyId`: il companyId dell'azienda correntemente selezionata
 * - `setPageCompanyId`: la funzione per cambiare l'azienda correntemente selezionata
 * - `error`: l'eventuale errore verificatosi durante l'autenticazione
 * - `authStatus`: lo stato corrente dell'autenticazione
 * - `logout`: la funzione per effettuare la logout
 * - `login`: la funzione per effettuare la login
 * - `init`: la funzione per inizializzare l'autenticazione
 * - `changeGroup`: la funzione per cambiare l'azienda selezionata
 * - `setToken`: la funzione per impostare il token di autenticazione
 * - `getToken`: la funzione per ottenere il token di autenticazione
 * - `refreshToken`: la funzione per rinnovare il token di autenticazione
 * - `sessionExpired`: la funzione per gestire l'eventuale scadenza della sessione
 * - `forgotPassword`: la funzione per richiedere la reimpostazione della password
 * - `resetPassword`: la funzione per reimpostare la password
 */

export const useAuth = () => {
  const pageCompanyId = useAuthStore((s) => s.pageCompanyId);
  const error = useAuthStore((s) => s.error);
  const authStatus = useAuthStore((s) => s.authStatus);
  const username = useAuthStore((s) => s.username);
  const persistentLogin = useAuthStore((s) => s.persistentLogin);
  const currentMfaType = useAuthStore((s) => s.currentMfaType);
  const totpSecret = useAuthStore((s) => s.totpSecret);
  const totpQrUri = useAuthStore((s) => s.totpQrUri);
  const requiresMfaStepUp = useAuthStore((s) => s.requiresMfaStepUp);
  const mfaMaskedEmail = useAuthStore((s) => s.mfaMaskedEmail);
  const isChangingGroup = useAuthStore((s) => s.isChangingGroup);

  const setMfaMaskedEmail = useAuthStore((s) => s.setMfaMaskedEmail);
  const setRequiresMfaStepUp = useAuthStore((s) => s.setRequiresMfaStepUp);
  const setAuthStatus = useAuthStore((s) => s.setAuthStatus);
  const setPageCompanyId = useAuthStore((s) => s.setPageCompanyId);
  const setError = useAuthStore((s) => s.setError);
  const setUsername = useAuthStore((s) => s.setUsername);
  const setPersistentLogin = useAuthStore((s) => s.setPersistentLogin);
  const setCurrentMfaType = useAuthStore((s) => s.setCurrentMfaType);
  const setTotpSecret = useAuthStore((s) => s.setTotpSecret);
  const setTotpQrUri = useAuthStore((s) => s.setTotpQrUri);
  const setIsChangingGroup = useAuthStore((s) => s.setIsChangingGroup);
  const navigate = useNavigate();

  // ATTENZIONE! NECESSARIO FARE DESTRUCTURING E METTERE IN USECALLBACK SOLO LA MUTATEASYNC perchè altrimenti l'oggetto ritornato
  // dalla useMutation cambia ad ogni istanza causando il cambio delle useCallback (loop se sono usate in useEffect).
  // Hanno senso le mutation in un contesto globale al di fuori di un componente? Non molto, l'unico valore aggiunto è probabilmente
  // il retry gratuito e aver a disposizione dei flag per sapere lo stato della request
  const { mutateAsync: logoutMutationAsync } = useLogoutMutation();
  const {
    mutateAsync: loginMutationAsync,
    // isPending: loginIsPending,
    // isSuccess: loginIsSuccess,
  } = useLoginMutation();

  const {
    mutateAsync: verifyMfaMutationAsync,
    // isPending: loginIsPending,
    // isSuccess: loginIsSuccess,
  } = useVerifyMfaMutation();

  const {
    mutateAsync: startMfaSetupMutationAsync,
    // isPending: loginIsPending,
    // isSuccess: loginIsSuccess,
  } = useStartMfaSetupMutation();

  const {
    mutateAsync: confirmMfaSetupMutationAsync,
    // isPending: loginIsPending,
    // isSuccess: loginIsSuccess,
  } = useConfirmMfaSetupMutation();

  const { mutateAsync: forgotPasswordMutationAsync } =
    useForgotPasswordMutation();
  const { mutateAsync: resetPasswordMutationAsync } =
    useResetPasswordMutation();
  const { mutateAsync: changeGroupMutationAsync } = useChangeGroupMutation();

  const queryClient = useQueryClient();
  const t = useTranslate();

  const init = useCallback(async () => {
    try {
      const result = await refreshTokenRequest();
      setToken(result.jwtToken!);
      setUsername(result.username);
      setAuthStatus(AuthenticationStatus.Authenticated);

      //   // 2. ORA, chiami l'endpoint ponte per inizializzare la sessione di MoxieManager
      // try {
      //   const response = await fetch('http://localhost:8080/Home/InitializeMoxieSession', {
      //     method: 'POST',
      //     // Questo è il pezzo FONDAMENTALE per inviare il cookie
      //     credentials: 'include',
      //     headers: {
      //       // Potrebbe non essere necessario, ma è buona pratica
      //       'Content-Type': 'application/json'
      //     }
      //   });

      //   if (!response.ok) {
      //     // Se il server risponde con un errore (es. 401 Unauthorized, 500)
      //     throw new Error('Errore durante l\'inizializzazione della sessione di MoxieManager.');
      //   }

      //   const result = await response.json();

      //   if (result.success) {
      //     console.log(result.message);
      //   } else {
      //     throw new Error(result.message || 'La sessione non è stata inizializzata.');
      //   }

      // } catch (err) {
      //   console.error(err);
      // }
    } catch {
      setToken(null);
      setAuthStatus(AuthenticationStatus.Unauthenticated);
    }
  }, [setAuthStatus, setUsername]);

  const sessionExpired = useCallback(async () => {
    setAuthStatus(AuthenticationStatus.Unauthenticated);
    setToken(null);
    setError("");
    setUsername(null);
    showErrorFeedback("Session expired");
    // Avoid refresh token interceptor error
    setTimeout(() => {
      queryClient.resetQueries();
    });
  }, [setAuthStatus, setError, setUsername, queryClient]);

  const logout = useCallback(async () => {
    try {
      await logoutMutationAsync();
    } finally {
      setAuthStatus(AuthenticationStatus.Unauthenticated);
      setToken(null);
      setUsername(null);
      setError("");
      // Avoid refresh token interceptor error
      setTimeout(() => {
        queryClient.resetQueries();
      });
    }
  }, [logoutMutationAsync, setAuthStatus, setError, setUsername, queryClient]);

  const login = useCallback(
    async (username: string, password: string, remember: boolean) => {
      try {
        const result = await loginMutationAsync({
          username,
          password,
          remember,
        });
        setError("");
        setUsername(result.username);
        if (result.requiresMFA) {
          setPersistentLogin(remember);
          setCurrentMfaType(result.mfaType);
          setMfaMaskedEmail(result.mfaEmail || null);
          navigate("/account/mfa");
        } else {
          setToken(result.jwtToken!);

          if (result.configureMFA) {
            setAuthStatus(AuthenticationStatus.SetupMfa);
            setCurrentMfaType(result.mfaType);
            navigate("/account/start-mfa-setup");
          } else {
            setCurrentMfaType(null);
            setAuthStatus(AuthenticationStatus.Authenticated);
          }
        }
        return result;
      } catch (error) {
        setToken(null);
        setAuthStatus(AuthenticationStatus.Unauthenticated);
        setError(
          error instanceof ApiError ? error.message : t("common.genericError")
        );
      }
    },
    [
      loginMutationAsync,
      setAuthStatus,
      setError,
      t,
      setUsername,
      setPersistentLogin,
      setCurrentMfaType,
      navigate,
      setMfaMaskedEmail,
    ]
  );

  const verifyMfa = useCallback(
    async (code: string) => {
      try {
        const result = await verifyMfaMutationAsync({
          username: username!,
          mfaCode: code,
          remember: persistentLogin,
        });

        setToken(result.jwtToken!);
        setAuthStatus(AuthenticationStatus.Authenticated);
        setError("");
        return result;
      } catch (error) {
        setError(
          error instanceof ApiError ? error.message : t("common.mfaError")
        );
        return null;
      }
    },
    [
      verifyMfaMutationAsync,
      setAuthStatus,
      setError,
      t,
      username,
      persistentLogin,
    ]
  );

  const stepUpMfa = useCallback(
    async (code: string) => {
      try {
        const result = await StepUpRequest({ mfaCode: code });
        setToken(result.jwtToken!);
        setRequiresMfaStepUp(false, null, null);
        processMfaQueue(result.jwtToken!);
        return result;
      } catch (error) {
        setError(
          error instanceof ApiError ? error.message : t("common.genericError")
        );
        return null;
      }
    },
    [setError, setRequiresMfaStepUp, t]
  );

  const startMfaSetup = useCallback(
    async (email?: string) => {
      try {
        const result = await startMfaSetupMutationAsync({
          mfaType: currentMfaType!,
          email,
        });

        if (result.mfaType === 0) {
          setTotpSecret(result.secret!);
          setTotpQrUri(result.qrCodeUri!);
        } else {
          setTotpSecret(null);
          setTotpQrUri(null);
          navigate("/account/mfa");
        }

        setError("");
        return result;
      } catch (error) {
        setError(
          error instanceof ApiError ? error.message : t("common.mfaError")
        );
        return null;
      }
    },
    [
      startMfaSetupMutationAsync,
      currentMfaType,
      setError,
      t,
      setTotpSecret,
      setTotpQrUri,
      navigate,
    ]
  );

  const confirmMfaSetup = useCallback(
    async (code: string) => {
      try {
        const result = await confirmMfaSetupMutationAsync({
          code,
          mfaType: currentMfaType!,
        });

        setAuthStatus(AuthenticationStatus.Authenticated);
        setToken(result.jwtToken);
        setError("");
        setTotpQrUri(null);
        setTotpSecret(null);
        setCurrentMfaType(null);
        return result;
      } catch (error) {
        setError(
          error instanceof ApiError ? error.message : t("common.mfaError")
        );
        return null;
      }
    },
    [
      confirmMfaSetupMutationAsync,
      currentMfaType,
      setError,
      t,
      setAuthStatus,
      setCurrentMfaType,
      setTotpQrUri,
      setTotpSecret,
    ]
  );

  const forgotPassword = useCallback(
    async (email: string) => {
      try {
        await forgotPasswordMutationAsync({
          email,
        });
        setError("");
        return true;
      } catch (error) {
        setToken(null);
        setAuthStatus(AuthenticationStatus.Unauthenticated);
        setError(
          error instanceof ApiError ? error.message : t("common.genericError")
        );
        return false;
      }
    },
    [forgotPasswordMutationAsync, setAuthStatus, setError, t]
  );

  const resetPassword = useCallback(
    async (token: string, newPassword: string) => {
      try {
        await resetPasswordMutationAsync({
          newPassword,
          token,
        });
        setError("");
        return true;
      } catch (error) {
        setToken(null);
        setAuthStatus(AuthenticationStatus.Unauthenticated);
        setError(
          error instanceof ApiError ? error.message : t("common.genericError")
        );
        return false;
      }
    },
    [resetPasswordMutationAsync, setAuthStatus, setError, t]
  );

  const changeGroup = useCallback(
    async (groupId: number) => {
      try {
        setPageCompanyId(null);
        const result = await changeGroupMutationAsync({
          groupId,
        });
        setToken(result.jwtToken);
        navigate("/");
      } catch (error) {
        showErrorFeedback(
          error instanceof ApiError ? error.message : t("common.genericError")
        );
      }
    },
    [changeGroupMutationAsync, t, navigate, setPageCompanyId]
  );

  const signUp = useCallback(
    async (
      token: string,
      newPassword: string,
      name: string,
      surname: string
    ) => {
      try {
        await signUpRequest({
          newPassword,
          token,
          name,
          surname,
        });
        setError("");
        return true;
      } catch (error) {
        setToken(null);
        setAuthStatus(AuthenticationStatus.Unauthenticated);
        setError(
          error instanceof ApiError ? error.message : t("common.genericError")
        );
        return false;
      }
    },
    [setAuthStatus, setError, t]
  );

  return {
    pageCompanyId,
    setPageCompanyId,
    error,
    setError,
    authStatus,
    logout,
    login,
    // loginIsPending,
    // loginIsSuccess,
    init,
    changeGroup,
    isChangingGroup,
    setToken,
    getToken,
    refreshToken,
    sessionExpired,
    forgotPassword,
    resetPassword,
    signUp,
    verifyMfa,
    currentMfaType,
    startMfaSetup,
    confirmMfaSetup,
    totpSecret,
    totpQrUri,
    stepUpMfa,
    requiresMfaStepUp,
    setRequiresMfaStepUp,
    setIsChangingGroup,
    mfaMaskedEmail,
  };
};
